import { Component, ElementRef, HostBinding, ViewChild, Inject, ComponentRef } from '@angular/core';
import { SynchronizationService } from '../../services/synchronization.service';
import { ProjectService } from '../../services/project.service';
import SelectableComponent from '../../interfaces/selectableComponent';
import { CommunicationService } from '../../services/communication.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../services/settings.service';
import { Sprint } from '../../models/sprint';
import { EventSprintcardClosed } from '../../models/syncEvents/realtimeEvents/eventSprintcardClosed';
import { SprintService } from '../../services/sprint.service';
import { EventSprintCreated } from '../../models/syncEvents/persistenceEvents/eventSprintCreated';
import { EventSprintDeleted } from '../../models/syncEvents/persistenceEvents/eventSprintDeleted';

@Component({
  selector: 'app-sprintcard',
  templateUrl: './sprintcard.component.html',
  styleUrls: ['./sprintcard.component.scss']
  })
export class SprintcardComponent implements SelectableComponent {
  public _sprint: Sprint;
  public sprintHasBeenChanged = false;
  public formIsValid = true;
  public formErrorMessage: String = '';
  // For new issues that are being created and dont have an id and corresponding jira-issue yet
  public isNewSprint = false;
  public originalSprint: Sprint;
  public componentRef: ComponentRef<SprintcardComponent>;

  @HostBinding('style.z-index')
  public zIndex;

  constructor(
    @Inject('sprint') public sprintParameter: Sprint,
    @Inject('projectKey') public readonly projectKey: string,
    @Inject('onCloseCallback') public readonly onCloseCallback,
    @Inject('onNewSprintCreatedCallback') public readonly onNewSprintCreatedCallback,
    @Inject('onSprintRemovedCallback') public readonly onSprintRemovedCallback,
    protected projectService: ProjectService,
    protected sprintService: SprintService,
    protected settingsService: SettingsService,
    protected communicationService: CommunicationService,
    protected syncService: SynchronizationService,
    protected elementRef: ElementRef,
    protected translate: TranslateService,
    protected dialog: MatDialog) {

    this.sprint = sprintParameter;
  }

  static getContainer(): HTMLElement {
    return document.getElementById('issuecardContainer');
  }

  /**
   * Returns an ElementReference to the host-element of this component for DOM-access
   */
  getElementRef(): ElementRef {
    return this.elementRef;
  }

  /**
   * Close and destroy this sprintcard.
   */
  closeThisCard() {
    if (this.sprintHasBeenChanged) {
      this.saveChanges();
    }
    this.onCloseCallback(this);
    this.syncService.sendRealTimeEventNew(new EventSprintcardClosed(this.sprint.id));
  }

  discardChangesAndCloseThisCard() {
    this.discardChanges();
    this.onCloseCallback(this);
  }

  onDiscardButtonClicked() {
    this.discardChangesAndCloseThisCard();
    this.syncService.sendRealTimeEventNew(new EventSprintcardClosed(this.sprint.id));
  }

  update(updatedSprint: Sprint) {
    // We assume the sprint will be changed. This might not actually be true if (updatedSprint == our sprint)
    this.sprintHasBeenChanged = true;
    this.sprint.name = updatedSprint.name;
    this.sprint.startDate = updatedSprint.startDate;
    this.sprint.endDate = updatedSprint.endDate;
    this.validateForm();
  }

  createNewSprint() {
    const temporarySprintId = this.sprint.id;
    this.sprintService.createSprint(this.projectKey, this.sprint).subscribe((createdSprint: Sprint) => {
      if (createdSprint) {
        this.sprint = createdSprint;
        this.isNewSprint = false;
        this.onNewSprintCreatedCallback(this.sprint);
        const syncEvent = new EventSprintCreated(this.sprint.id, temporarySprintId);
        this.syncService.sendPersistenceEventNew(syncEvent);
      }
      else {
        console.error('sprint could NOT be created.');
      }
      this.onCloseCallback(this);
    });
  }

  deleteThisSprint() {
    this.sprintService.deleteSprint(this.projectKey, this.sprint.id).subscribe(() => {
      this.closeThisCard();
      this.onSprintRemovedCallback(this.sprint.id);
      const syncEvent = new EventSprintDeleted(this.sprint.id);
      this.syncService.sendPersistenceEventNew(syncEvent);
    });
  }

  openConfirmDeleteDialog(event): void {
    const title = this.translate.instant('components.sprintcard.deleteConfirmationTitle');
    const message = this.translate.instant('components.sprintcard.deleteConfirmationMessage');
    const okText = this.translate.instant('components.sprintcard.deleteConfirmationOkText');
    const cancelText = this.translate.instant('components.sprintcard.deleteConfirmationCancelText');
    // The button stays highlighted after we dismiss the dialog. This does not look good so we
    // need to remove the focus manually here
    event.srcElement.closest('button').blur();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { title: title, message: message, buttonTextConfirm: okText, buttonTextCancel: cancelText }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.deleteThisSprint();
      }
    });
  }

  sprintNameChanged(event: any): void {
    const value = event.target.value;
    this.sprint.name = value;
    this.sprintHasBeenChanged = true;
    this.validateForm();
    // TODO send realtime event for sprint
    // this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue));
  }

  sprintStartDateChanged(event: any): void {
    this.sprint.startDate = (event) ? event.target.value : undefined;
    this.sprintHasBeenChanged = true;
    this.validateForm();
    // TODO send realtime event for sprint
    // this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue));
  }

  sprintEndDateChanged(event: any): void {
    this.sprint.endDate = (event) ? event.target.value : undefined;
    this.sprintHasBeenChanged = true;
    this.validateForm();
    // TODO send realtime event for sprint
    // this.syncService.sendRealTimeEventNew(new EventIssuecardUpdated(this.issue.id, this.issue));
  }

  saveChanges() {
    if (this.sprintHasBeenChanged) {
      this.sprintHasBeenChanged = false;
      this.sprintService.updateSprint(this.projectKey, this._sprint);
    }
  }

  /* This method is triggered when sync-event from another client is received. We DONT send a sync-event of our own */
  discardChanges() {
    this.sprintHasBeenChanged = false;
    this.sprint.name = this.originalSprint.name;
    this.sprint.startDate = this.originalSprint.startDate;
    this.sprint.endDate = this.originalSprint.endDate;
  }

  /**
   * Sets the task for this issuecard and triggers loading of additional task information (attachments & comments)
   */
  public set sprint(sprint: Sprint) {
    this._sprint = sprint;
    this.originalSprint = Sprint.getCopy(this._sprint);
    // New, not yet persisted issues have a temporary id which is a negative number
    this.isNewSprint = (this.sprint.id < 0);
  }

  public get sprint() {
    return this._sprint;
  }

  // TODO this is not complete or elegant but validation will change with Upgrade to Angular v12
  // when we can your date-range instead of separate start- and end-date.
  validateForm() {
    this.formIsValid = true;
    this.formErrorMessage = '';

    if (!this.isValidSprintName()) {
      this.formIsValid = false;
      this.formErrorMessage += this.translate.instant('components.sprintcard.formErrorSprintName');
    }
    if (!this.isValidEndDate()) {
      this.formIsValid = false;
      this.formErrorMessage += this.translate.instant('components.sprintcard.formErrorSprintEndDate');
    }
  }

  private isValidSprintName(): boolean {
    const name = this.sprint.name;
    const isValid = (name !== undefined) && (name !== null) && (name !== '');
    return isValid;
  }

  private isValidEndDate(): boolean {
    // If both dates are set, startDate must not be after endDate
    const bothDatesAreSet = (this.sprint.startDate != null) && (this.sprint.endDate != null);
    const isValid = (bothDatesAreSet && (new Date(this.sprint.startDate) > new Date(this.sprint.endDate))) ? false : true;
    return isValid;
  }

}
